
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="0" y="0" width="8" height="8" fill="#F25022" />
    <rect x="10" y="0" width="8" height="8" fill="#7FBA00" />
    <rect x="0" y="10" width="8" height="8" fill="#00A4EF" />
    <rect x="10" y="10" width="8" height="8" fill="#FFB900" />
  </svg>
);

const Hero = React.memo(() => {
  return (
    <section
      aria-labelledby="hero-title"
      className="relative pt-24 pb-16 md:pt-32 md:pb-28 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden w-full"
    >
      {/* Subtle dot-grid backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#422680 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Massive EDUC4TE watermark — left-anchored edge-bleed typography (2026 trend) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 flex items-center select-none -ml-4 sm:-ml-6 md:-ml-10"
      >
        <span
          className="font-black tracking-[-0.05em] leading-none whitespace-nowrap"
          style={{
            fontSize: 'clamp(10rem, 22vw, 22rem)',
            background:
              'linear-gradient(180deg, rgba(66,38,128,0.08) 0%, rgba(245,73,82,0.05) 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          EDUC4TE
        </span>
      </div>

      <div className="relative max-w-5xl mx-auto text-center">
        {/* Trust row — pill + Microsoft Partner */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-6 md:mb-8">
          <span className="inline-flex items-center bg-[#F54952]/10 text-[#F54952] px-3 py-1.5 rounded-full text-[10px] md:text-xs font-bold tracking-[0.18em] uppercase">
            Australian Cybersecurity
          </span>
          <span
            aria-label="Microsoft Learn Partner"
            className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm"
          >
            <MicrosoftLogo />
            <span className="text-[10px] md:text-xs font-bold tracking-[0.12em] uppercase text-[#1a1a1a]">
              Microsoft Learn Partner
            </span>
          </span>
        </div>

        <h1
          id="hero-title"
          className="font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl mb-6 md:mb-8 leading-[1.02] tracking-[-0.025em]"
        >
          <span className="text-[#1a1a1a]">Train. Assess.</span>
          <br />
          <span className="font-extrabold text-[#F54952]">Stay Ahead.</span>
          <span className="sr-only"> Enterprise cybersecurity training and Microsoft 365 security assessments for Australian organisations.</span>
        </h1>

        <p className="text-base md:text-xl text-gray-600 mb-8 md:mb-12 leading-relaxed max-w-2xl mx-auto">
          Enterprise cybersecurity training and fixed-price security
          assessments — built for Australian compliance, delivered by
          practitioners.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
          <Link to="/training" className="w-full sm:w-auto">
            <Button
              size="lg"
              className="w-full bg-[#F54952] hover:bg-[#660F56] text-white text-base md:text-lg px-7 md:px-9 py-5 md:py-6 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#F54952]/25"
            >
              Start Training Today
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
          <Link to="/assessments" className="w-full sm:w-auto">
            <Button
              size="lg"
              variant="outline"
              className="w-full border-2 border-[#422680] text-[#422680] hover:bg-[#422680] hover:text-white text-base md:text-lg px-7 md:px-9 py-5 md:py-6 transition-all"
            >
              View Assessments
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
});

Hero.displayName = 'Hero';
export default Hero;
